<script lang="ts">
  // Snapshots: every point of the track (logic/track.ts), to pick two and
  // open their interdiff in the Files view. Where the track came from is
  // the page's business, not this one's.
  import type { Track } from '../lib/logic/track';
  import { comparisonOf, comparisonQuery, timelineOf, type RangePoint } from '../lib/logic/range';
  import { carryFilter } from '../lib/logic/filefilter';
  import { relTime } from '../lib/logic/time';
  import { nav } from '../lib/nav.svelte';
  import type { Facets } from '../lib/subject';
  import type { LinkedPull } from '../lib/state/compare/fetch';
  import type { ChecksState } from '../lib/logic/checks';

  interface Props {
    track: Track;
    /** The subject's facets: where a range opens when the URL names no `from`. */
    facets: Facets;
    /** The Files view's path, which a picked range opens (as `from`/`to`). */
    filesPath: string;
    /**
     * A point's number, where the subject numbers its heads (a pull
     * request's snapshots, which its comments name): null for a point it
     * does not number. Without it, points are numbered in track order.
     */
    numberOf?: (sha: string) => number | null;
    /**
     * The pull requests that brought a commit in, where that is a facet of
     * the subject (`linkedPulls`): undefined for a commit not read (yet),
     * empty for one no pull request holds.
     */
    linked?: (sha: string) => readonly LinkedPull[] | undefined;
    /** Why the linked pull requests could not be read, when they could not. */
    linkedError?: string | null;
    /**
     * How many of the subject's commits the track holds, where it holds
     * fewer than all of them between its base and its head (a compare of
     * more than it reads, whose lane breaks before the first held point):
     * the view says so. Null where it holds every one.
     */
    held?: { held: number; total: number } | null;
    /** A commit's checks (GitHub's rollup), where the page read them: undefined for a commit not read (yet). */
    checks?: (sha: string) => ChecksState | undefined;
    /**
     * Reading older commits than the track holds (a branch history, read a
     * window at a time): whether more can be read, one being read, why the
     * last could not be, and how to read one. Null where the page reads none.
     */
    older?: { can: boolean; loading: boolean; error: string | null; load: () => void } | null;
    /** What else the view says of its commits, a line each (where their force-pushes come from, what the list leaves out). */
    notes?: readonly string[];
  }
  let { track, facets, filesPath, numberOf, linked, linkedError = null, held = null, checks, older = null, notes = [] }: Props = $props();
  /** What the view says it leaves out: the commits between the base and the first held point, or (a window) those older than the base. */
  const leftOut = $derived(
    held ? ` (the newest ${held.held} of ${held.total}: older ones are not shown)` : track.hasOlder ? (older?.can ? ' (the newest: Load older reads more)' : ' (the newest only: older ones are not shown)') : '',
  );
  const CHECKS_TEXT: Record<ChecksState, string> = { success: 'Checks passed', failure: 'Checks failed', pending: 'Checks running', none: 'No checks' };
  /** What the view and its points are called (the subject's `points` facet). */
  const commitsNamed = $derived(facets.points === 'commits');
  const title = $derived(commitsNamed ? 'Commits' : 'Snapshots');
  const pointName = $derived(commitsNamed ? 'Commit' : 'Snapshot');

  const timeline = $derived(timelineOf(track, facets));
  /** The track's points but the base, oldest first, each replaced run after the commit it forks off. */
  const snaps = $derived(timeline.points.filter((p) => p.value !== 'base'));
  const base = $derived(track.base);
  /** Where a range starts when the URL names no `from`: the base, or (a branch history) the head's parent. */
  const opensFrom = $derived(comparisonOf(timeline, new URLSearchParams()).from ?? 'base');
  /**
   * The picked ends, by URL value: `base` or a SHA, or '' for the default
   * (where the range opens; the head), whichever commit that is when
   * Compare is pressed.
   */
  let from = $state('');
  let to = $state('');
  const fromValue = $derived(from || opensFrom);
  const toValue = $derived(to || track.head);
  /** A row's radio value: '' for the end it is by default. */
  const fromRadio = (value: string) => (value === opensFrom ? '' : value);

  /** The Files view of a range: `filesPath` (whose own query, a view, stays) with the range's ends, and the file filter carried. */
  function href(f: string, t: string): string {
    const ends = comparisonQuery(timeline, f, t);
    const url = new URL(filesPath, 'http://app');
    for (const [k, v] of Object.entries(ends)) {
      if (v === null) url.searchParams.delete(k);
      else url.searchParams.set(k, v);
    }
    return carryFilter(url.pathname + url.search, nav.query);
  }

  const valid = $derived(fromValue !== toValue);

  function label(p: RangePoint): string {
    return p.message ?? (p.lane === 'replaced' ? 'Before force-push' : p.head ? 'Head' : 'Commit');
  }

  function meta(p: RangePoint): string {
    const kind = p.lane === 'replaced' ? 'replaced by a force-push' : p.forcePushedBy ? 'force-push' : 'commit';
    const who = p.lane === 'main' && p.forcePushedBy ? p.forcePushedBy : p.author;
    return [kind, who, p.at ? relTime(p.at) : null, p.head ? 'head' : null].filter(Boolean).join(' · ');
  }
</script>

<section class="snapshots">
  <div class="sectionhead">
    <div>
      <h2 class="section-title">{title}</h2>
      <p class="muted small">
        {#if commitsNamed}
          Every commit from the base to the head{leftOut}, with the pull requests
          that brought it in. Pick two to see what changed between them.
        {:else}
          Every commit, and every head a force-push replaced. Pick two to see the interdiff, computed from the files themselves
          so rebases don't drown it in upstream changes.
        {/if}
      </p>
      {#each notes as note (note)}<p class="muted small">{note}</p>{/each}
      {#if linkedError}<p class="small linked-error" role="alert">{linkedError}</p>{/if}
    </div>
    <div class="compare-bar">
      <span class="mono small">{fromValue === 'base' ? 'base' : fromValue.slice(0, 7)} → {toValue === track.head ? 'head' : toValue.slice(0, 7)}</span>
      <button class="btn primary" disabled={!valid} onclick={() => nav.go(href(fromValue, toValue))}>Compare</button>
    </div>
  </div>
  {#if older && track.hasOlder}
    <div class="older-bar">
      {#if older.can}
        <button class="btn small" disabled={older.loading} onclick={() => older.load()}>{older.loading ? 'Loading older commits…' : 'Load older commits'}</button>
      {:else}
        <span class="muted small">Older commits exist; Changeling reads the newest of a history only.</span>
      {/if}
      {#if older.error}<span class="small linked-error" role="alert">{older.error}</span>{/if}
    </div>
  {/if}
  <div class="snap-table" role="table" aria-label={title}>
    <div class="snap-row head" role="row">
      <span role="columnheader">From</span><span role="columnheader">To</span><span role="columnheader">{pointName}</span><span role="columnheader"></span>
    </div>
    <div class="snap-row" role="row">
      <span role="cell"><input type="radio" name="from" value={fromRadio('base')} bind:group={from} aria-label="Compare from base" /></span>
      <span role="cell"></span>
      <span role="cell" class="snap-main"><b>Base</b> <span class="muted">{base.name} ({facets.opens === 'all' ? 'merge base' : 'before the oldest commit shown'})</span></span>
      <span role="cell"></span>
    </div>
    {#each snaps as s, i (s.sha)}
      {@const n = numberOf ? numberOf(s.sha) : i + 1}
      <div class="snap-row" role="row" class:current={s.head}>
        <span role="cell"><input type="radio" name="from" value={fromRadio(s.sha)} bind:group={from} aria-label="Compare from {s.short}" /></span>
        <span role="cell"><input type="radio" name="to" value={s.head ? '' : s.sha} bind:group={to} aria-label="Compare to {s.short}" /></span>
        <span role="cell" class="snap-main">
          {#if n !== null}<b class="snap-n" title="{pointName} {n}">{n}</b>{/if}
          <code>{s.short}</code>
          <span class="snap-label">{label(s)}</span>
          <span class="muted small">{meta(s)}</span>
          {#if checks}
            {@const c = checks(s.sha)}
            {#if c && c !== 'none'}<span class="snap-checks {c}" role="img" aria-label={CHECKS_TEXT[c]} title={CHECKS_TEXT[c]}></span>{/if}
          {/if}
          {#if facets.linkedPulls}
            {@const pulls = linked?.(s.sha)}
            {#each pulls ?? [] as p (p.number)}
              {#if p.path}<a class="snap-pull" href={p.path} title="{p.title} ({p.state.toLowerCase()})">#{p.number}</a>{:else}<a class="snap-pull" href={p.url} target="_blank" rel="noreferrer" data-external title="{p.title} ({p.state.toLowerCase()})">#{p.number}</a>{/if}
            {/each}
          {/if}
        </span>
        <span role="cell" class="snap-links">
          <a href={href('base', s.sha)}>Diff</a>
          {#if i > 0}<a href={href(snaps[i - 1].sha, s.sha)}>Since previous</a>{/if}
          {#if !s.head}<a href={href(s.sha, track.head)}>Since this</a>{/if}
        </span>
      </div>
    {/each}
  </div>
</section>
